/**
 * Sentry initialization shared between the embed bundle (UE WebView) and
 * the standalone marketing/console bundle.
 *
 * 2026-06-06: introduced to give the V3 dev session real error visibility
 * (the agent can poll the Sentry MCP for issues instead of you pasting
 * console errors) AND to lay the groundwork for production error tracking
 * once GDB launches with opt-in telemetry.
 *
 * Dev: errors stream live as you click around — visible in the Sentry UI
 * and queryable via the MCP. No network round-trip until a real error
 * fires.
 *
 * Prod (post-launch): the user toggle gating ingestion is enforced at the
 * `enabled` flag below. Until that toggle exists, prod builds keep
 * Sentry initialized but `sampleRate` controlled by env so we can ship
 * 0% sampling at first and dial up after the opt-in UX lands.
 */

import * as Sentry from '@sentry/react'

const env = import.meta.env

/**
 * Picks the right DSN for the bundle:
 * - `embed`      → VITE_SENTRY_DSN_EMBED (→ gamedevbuddy-plugin project)
 * - `standalone` → VITE_SENTRY_DSN_WEB   (→ gamedevbuddy project)
 *
 * Falls back to a single VITE_SENTRY_DSN if a per-bundle DSN isn't set,
 * which keeps the env minimal for contributors who don't care which
 * Sentry project receives their dev errors.
 */
function resolveDsn(bundle: 'embed' | 'standalone'): string | undefined {
  const perBundle =
    bundle === 'embed'
      ? (env.VITE_SENTRY_DSN_EMBED as string | undefined)
      : (env.VITE_SENTRY_DSN_WEB as string | undefined)
  return perBundle || (env.VITE_SENTRY_DSN as string | undefined)
}

export function initSentry(bundle: 'embed' | 'standalone') {
  const dsn = resolveDsn(bundle)
  if (!dsn) {
    if (env.DEV) {
      // Loud in dev so we notice if the DSN somehow drops out — silent
      // failure would defeat the whole "agent can see errors" goal.
      console.warn(
        `[sentry] no DSN set for bundle '${bundle}' — error tracking disabled`,
      )
    }
    return
  }

  Sentry.init({
    dsn,
    environment: env.MODE,           // 'development' / 'production' / etc.
    release: env.VITE_APP_VERSION as string | undefined,
    // `bundle` still tags every event for cross-project search even though
    // we now route to separate projects per bundle. Useful when running
    // Sentry queries across both projects from the MCP.
    initialScope: { tags: { bundle } },
    // Lightweight defaults for now. We can layer on
    // `Sentry.browserTracingIntegration()` and replay later once the opt-in
    // UX is in place — both are off by default to keep the surface minimal.
    integrations: [],
    tracesSampleRate: 0,
    // Send breadcrumbs (console, navigation, fetch) so stack traces have
    // context — these don't ship without a captured error.
    sendDefaultPii: false,
  })
}
